import LoadingSpinner from '@/components/LoadingSpinner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { PLAN_MONTHLY_PRICES_USD, PlanKey } from '@/lib/billing/plans'
import { useBillingPlanPrices } from '@/lib/hooks/useBillingPlanPrices'
import {
  describeReferralUpgradeOffer,
  formatBillingPriceParts,
  getReferralDiscountedAmount,
  useReferralUpgradeOffer,
} from '@/lib/hooks/useReferralUpgradeOffer'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import {
  getActiveOffer,
  isTrialWinbackOffer,
  TRIAL_WINBACK_DISCOUNT_MONTHS,
  TRIAL_WINBACK_DISCOUNT_PERCENT,
} from '@/lib/billing/offers'
import { useSupabaseSession } from '@/lib/supabase/supabaseProvider'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpcVanilla } from '@/lib/trpc/vanilla'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Check,
  ClipboardCheck,
  DollarSign,
  Folders,
  Infinity as InfinityIcon,
  LucideIcon,
  Minus,
  Palette,
  Smartphone,
  UserRound,
  UsersRound,
  Zap,
} from 'lucide-react'
import { Fragment, useState } from 'react'

const selectablePlans: Array<Exclude<PlanKey, 'pro_monthly'>> = [
  'starter_monthly',
  'growth_monthly',
  'team_monthly',
]
const planRank: Record<Exclude<PlanKey, 'pro_monthly'>, number> = {
  starter_monthly: 0,
  growth_monthly: 1,
  team_monthly: 2,
}

type CompareValue = string | boolean
type PlanSummaryItem = { label: string; icon: LucideIcon }
type SelectablePlanKey = Exclude<PlanKey, 'pro_monthly'>

const planDisplay: Record<
  SelectablePlanKey,
  {
    title: string
    subtitle: string
    cta: string
    summary: PlanSummaryItem[]
  }
> = {
  starter_monthly: {
    title: 'Starter',
    subtitle: 'Best for solo coaches getting started.',
    cta: 'Select Plan',
    summary: [
      { label: 'Up to 20 clients', icon: UsersRound },
      { label: '1 coach seat (owner only)', icon: UserRound },
      { label: 'Client iOS and Android App', icon: Smartphone },
    ],
  },
  growth_monthly: {
    title: 'Growth',
    subtitle: 'Scale your coaching business with more capacity.',
    cta: 'Continue Coaching',
    summary: [
      { label: 'Up to 100 clients', icon: UsersRound },
      { label: '2 coach seats', icon: UserRound },
      { label: 'Custom branding', icon: Palette },
      { label: 'Integrated Payments', icon: DollarSign },
      { label: 'Lead Forms', icon: ClipboardCheck },
      { label: 'Resource collection', icon: Folders },
    ],
  },
  team_monthly: {
    title: 'Team',
    subtitle: 'For larger teams that need unlimited scale.',
    cta: 'Select Plan',
    summary: [
      { label: 'Unlimited client and coach seats', icon: InfinityIcon },
      { label: 'Custom branding', icon: Palette },
      { label: 'Integrated Payments', icon: DollarSign },
      { label: 'Lead Forms', icon: ClipboardCheck },
      { label: 'Resource collection', icon: Folders },
      { label: 'Priority support', icon: Zap },
    ],
  },
}

const compareSections: Array<{
  title: string
  rows: Array<{
    feature: string
    starter: CompareValue
    growth: CompareValue
    team: CompareValue
  }>
}> = [
  {
    title: 'Client Management',
    rows: [
      {
        feature: 'Active clients',
        starter: 'Up to 20',
        growth: 'Up to 100',
        team: 'Unlimited',
      },
      {
        feature: 'Coach seats (owner included)',
        starter: '1 seat',
        growth: '2 seats',
        team: 'Unlimited',
      },
      {
        feature: 'Mobile apps (iOS & Android)',
        starter: true,
        growth: true,
        team: true,
      },
      {
        feature: 'In-app messaging',
        starter: true,
        growth: true,
        team: true,
      },
      {
        feature: 'Integrated payments',
        starter: false,
        growth: true,
        team: true,
      },
      {
        feature: 'Resource collection',
        starter: false,
        growth: true,
        team: true,
      },
    ],
  },
  {
    title: 'Branding & Leads',
    rows: [
      {
        feature: 'Custom branding',
        starter: false,
        growth: true,
        team: true,
      },
      {
        feature: 'Lead forms',
        starter: false,
        growth: true,
        team: true,
      },
    ],
  },
  {
    title: 'Support',
    rows: [
      {
        feature: 'Email support',
        starter: true,
        growth: true,
        team: true,
      },
      {
        feature: 'Priority support',
        starter: false,
        growth: false,
        team: true,
      },
    ],
  },
]

export const Route = createFileRoute('/_auth/_dashboard/upgrade')({
  validateSearch: (search: Record<string, unknown>) => ({
    plan:
      typeof search.plan === 'string' &&
      selectablePlans.includes(search.plan as Exclude<PlanKey, 'pro_monthly'>)
        ? (search.plan as Exclude<PlanKey, 'pro_monthly'>)
        : undefined,
  }),
  component: UpgradePage,
})

function UpgradePage() {
  const navigate = useNavigate()
  const { plan } = Route.useSearch()
  const { subscription } = useSupabaseSession()
  const [selectedPlan, setSelectedPlan] = useState<SelectablePlanKey>(
    plan ?? 'growth_monthly',
  )
  const [isLoading, setIsLoading] = useState(false)
  const { data: planPricesData, isLoading: pricingLoading } =
    useBillingPlanPrices()
  const { data: referralOffer } = useReferralUpgradeOffer()
  const { data: entitlements } = useOrgEntitlements()
  const offer = getActiveOffer()

  const hasLaunchOffer = isTrialWinbackOffer(offer)
  const subscriptionStatus = subscription?.status ?? null
  const subscriptionMetadata =
    subscription?.metadata && typeof subscription.metadata === 'object'
      ? (subscription.metadata as Record<string, unknown>)
      : null
  const isInternalTrialSubscription = Boolean(
    !subscription?.price_id &&
      (subscriptionMetadata?.internal_trial === true ||
        subscriptionMetadata?.trial_source === 'internal'),
  )
  const hasStripeSubscription =
    Boolean(subscription?.id?.startsWith('sub_')) &&
    Boolean(subscription?.price_id) &&
    !isInternalTrialSubscription &&
    (subscriptionStatus === 'active' || subscriptionStatus === 'trialing')
  const currentPlanKey =
    hasStripeSubscription &&
    entitlements?.plan_key &&
    entitlements.plan_key !== 'pro_monthly' &&
    selectablePlans.includes(entitlements.plan_key)
      ? entitlements.plan_key
      : null
  const planPricing = {
    starter_monthly: {
      amount:
        planPricesData?.plan_prices?.starter_monthly?.amount ??
        PLAN_MONTHLY_PRICES_USD.starter_monthly,
      currency:
        planPricesData?.plan_prices?.starter_monthly?.currency?.toUpperCase() ??
        planPricesData?.display_currency?.toUpperCase() ??
        'USD',
    },
    growth_monthly: {
      amount:
        planPricesData?.plan_prices?.growth_monthly?.amount ??
        PLAN_MONTHLY_PRICES_USD.growth_monthly,
      currency:
        planPricesData?.plan_prices?.growth_monthly?.currency?.toUpperCase() ??
        planPricesData?.display_currency?.toUpperCase() ??
        'USD',
    },
    team_monthly: {
      amount:
        planPricesData?.plan_prices?.team_monthly?.amount ??
        PLAN_MONTHLY_PRICES_USD.team_monthly,
      currency:
        planPricesData?.plan_prices?.team_monthly?.currency?.toUpperCase() ??
        planPricesData?.display_currency?.toUpperCase() ??
        'USD',
    },
  } satisfies Record<
    SelectablePlanKey,
    { amount: number | null; currency: string }
  >

  const handleUpgrade = async (planKey: SelectablePlanKey) => {
    setSelectedPlan(planKey)
    setIsLoading(true)
    try {
      const origin = window.location.origin
      const data = await trpcVanilla.billing.upgradeTrial.mutate({
        redirect: window.location.href,
        successRedirect: `${origin}/clients`,
        cancelRedirect: `${origin}/upgrade${window.location.search}`,
        offer,
        planKey,
      })

      const checkoutUrl = data.session?.url
      if (!checkoutUrl) throw new Error('No session url')
      window.location.href = checkoutUrl
    } catch (error: unknown) {
      errorToast(
        error instanceof Error ? error : new Error('Unable to start checkout.'),
        'Unable to start checkout.',
      )
      setIsLoading(false)
    }
  }

  const renderCompareValue = (value: CompareValue) => {
    if (typeof value === 'string') {
      return (
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {value}
        </span>
      )
    }
    if (value) {
      return <Check className="mx-auto h-4 w-4 text-[#1456F0]" />
    }
    return <Minus className="mx-auto h-4 w-4 text-slate-400" />
  }

  return (
    <div className="min-h-svh w-full bg-slate-50 px-4 py-8 md:py-12 dark:bg-slate-950">
      <div className="mx-auto w-full max-w-7xl space-y-10">
        <div className="mx-auto max-w-3xl text-center">
          {referralOffer ? (
            <div className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 dark:bg-green-500/20 dark:text-green-200">
              <Zap className="mr-1 size-4" />
              Referral discount: {describeReferralUpgradeOffer(referralOffer)}
            </div>
          ) : (
            <div className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-600 dark:bg-orange-500/20 dark:text-orange-200">
              <Zap className="size-4 mr-1" /> Don&apos;t Miss Out: Special
              Launch Pricing
            </div>
          )}
          <h1 className="mt-5 text-4xl font-bold tracking-tight text-slate-900 md:text-5xl dark:text-slate-100">
            {hasStripeSubscription
              ? 'Manage Your Subscription'
              : 'Keep Your Premium Features'}
          </h1>
          <p className="mt-3 text-sm text-slate-600 md:text-base dark:text-slate-300">
            {hasStripeSubscription
              ? 'Compare plans and switch based on your current business needs.'
              : referralOffer
                ? 'Choose the plan that fits your coaching business. Your referral discount will be applied automatically at checkout.'
                : 'Choose the plan that fits your coaching business. Upgrade now and any remaining trial days are preserved at checkout.'}
          </p>
        </div>

        {referralOffer ? (
          <div className="mx-auto -mb-2 max-w-md rounded-full bg-green-100 px-4 py-2 text-center text-xs font-semibold text-green-700 dark:bg-green-500/20 dark:text-green-200">
            {describeReferralUpgradeOffer(referralOffer)} on all plans.
          </div>
        ) : hasLaunchOffer ? (
          <div className="mx-auto -mb-2 max-w-md rounded-full bg-green-100 px-4 py-2 text-center text-xs font-semibold text-green-700 dark:bg-green-500/20 dark:text-green-200">
            {TRIAL_WINBACK_DISCOUNT_PERCENT}% off all plans for first{' '}
            {TRIAL_WINBACK_DISCOUNT_MONTHS} months.
          </div>
        ) : null}

        <div className="grid gap-5 lg:grid-cols-3">
          {selectablePlans.map((planKey) => {
            const selected = selectedPlan === planKey
            const isPopular = planKey === 'growth_monthly'
            const isCurrentPlan = currentPlanKey === planKey
            const isUpgrade =
              currentPlanKey != null &&
              planRank[planKey] > planRank[currentPlanKey]
            const actionLabel =
              hasStripeSubscription && currentPlanKey
                ? isCurrentPlan
                  ? 'Current Plan'
                  : isUpgrade
                    ? 'Upgrade'
                    : 'Downgrade'
                : planDisplay[planKey].cta
            const monthlyPrice =
              planPricing[planKey].amount ?? PLAN_MONTHLY_PRICES_USD[planKey]
            const currency = planPricing[planKey].currency ?? 'USD'
            const referralDiscountedPrice = referralOffer
              ? getReferralDiscountedAmount(
                  monthlyPrice,
                  currency,
                  referralOffer,
                )
              : null
            const discountedPrice =
              referralDiscountedPrice ??
              (hasLaunchOffer
                ? Number(
                    (
                      (monthlyPrice * (100 - TRIAL_WINBACK_DISCOUNT_PERCENT)) /
                      100
                    ).toFixed(2),
                  )
                : null)
            const monthlyPriceParts = formatBillingPriceParts(
              monthlyPrice,
              currency,
            )
            const discountedPriceParts =
              discountedPrice != null
                ? formatBillingPriceParts(discountedPrice, currency)
                : null
            return (
              <div
                key={planKey}
                className={`relative flex h-full flex-col rounded-3xl border p-6 shadow-sm transition ${
                  isCurrentPlan
                    ? 'border-emerald-500 bg-emerald-50 text-slate-900 dark:border-emerald-400 dark:bg-emerald-500/10 dark:text-slate-100'
                    : isPopular
                      ? 'border-[#1456F0] bg-[#1456F0] text-white'
                      : 'border-slate-200 bg-white text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100'
                }`}
              >
                {isCurrentPlan ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-600 px-4 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                    Current Plan
                  </div>
                ) : null}
                {isPopular && !isCurrentPlan ? (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-orange-500 px-4 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                    Most Popular
                  </div>
                ) : null}
                <div className="flex flex-1 flex-col space-y-3">
                  <div>
                    <p className="text-2xl font-bold">
                      {planDisplay[planKey].title}
                    </p>
                    {pricingLoading && !planPricesData ? (
                      <div className="mt-3">
                        <Skeleton
                          className={`h-12 w-44 ${
                            isPopular && !isCurrentPlan
                              ? 'bg-white/30'
                              : 'bg-slate-200'
                          }`}
                        />
                        <Skeleton
                          className={`mt-2 h-4 w-16 ${
                            isPopular && !isCurrentPlan
                              ? 'bg-white/25'
                              : 'bg-slate-200'
                          }`}
                        />
                      </div>
                    ) : (
                      <div className="mt-3 flex items-end gap-1">
                        {discountedPriceParts ? (
                          <>
                            <span
                              className={`text-xl line-through ${
                                isPopular && !isCurrentPlan
                                  ? 'text-blue-200'
                                  : 'text-slate-400 dark:text-slate-500'
                              }`}
                            >
                              {monthlyPriceParts.symbol}
                              {monthlyPriceParts.value}
                            </span>
                            <span className="text-5xl font-black">
                              {discountedPriceParts.symbol}
                              {discountedPriceParts.value}
                            </span>
                          </>
                        ) : (
                          <span className="text-5xl font-black">
                            {monthlyPriceParts.symbol}
                            {monthlyPriceParts.value}
                          </span>
                        )}
                        <span
                          className={`pb-1 text-sm ${
                            isPopular && !isCurrentPlan
                              ? 'text-blue-100'
                              : 'text-slate-500 dark:text-slate-400'
                          }`}
                        >
                          /month
                        </span>
                      </div>
                    )}
                  </div>
                  <p
                    className={`text-sm ${
                      isPopular && !isCurrentPlan
                        ? 'text-blue-100'
                        : 'text-slate-600 dark:text-slate-300'
                    }`}
                  >
                    {planDisplay[planKey].subtitle}
                  </p>
                  <ul className="flex-1 space-y-2 pt-1">
                    {planDisplay[planKey].summary.map((feature) => {
                      const FeatureIcon = feature.icon
                      return (
                        <li
                          key={feature.label}
                          className={`flex items-center gap-2 text-sm ${
                            isPopular && !isCurrentPlan
                              ? 'text-white'
                              : 'text-slate-700 dark:text-slate-200'
                          }`}
                        >
                          <FeatureIcon
                            className={`h-4 w-4 ${
                              isPopular && !isCurrentPlan
                                ? 'text-white'
                                : 'text-[#1456F0]'
                            }`}
                          />
                          {feature.label}
                        </li>
                      )
                    })}
                  </ul>
                </div>
                <Button
                  className={`mt-8 h-11 w-full rounded-xl ${
                    isCurrentPlan
                      ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-500/20 dark:text-emerald-200 dark:hover:bg-emerald-500/20'
                      : isPopular
                        ? 'bg-white text-[#1456F0] hover:bg-slate-100'
                        : 'bg-[#1456F0] text-white hover:bg-[#0f49cd]'
                  }`}
                  disabled={isLoading || isCurrentPlan}
                  onClick={() => handleUpgrade(planKey)}
                >
                  {isLoading && selected ? <LoadingSpinner /> : null}
                  {isLoading && selected ? 'Loading...' : actionLabel}
                </Button>
              </div>
            )
          })}
        </div>

        <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
          Cancel anytime. All data saved.
        </p>

        <section className="rounded-3xl p-5 md:p-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Compare Features
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              Get a detailed look at what&apos;s included in each plan.
            </p>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full min-w-[760px] overflow-hidden rounded-2xl border border-slate-200 border-separate border-spacing-0 dark:border-slate-800">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/80">
                  <th className="px-4 py-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
                    Features
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Starter
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-[#1456F0]">
                    Growth
                  </th>
                  <th className="px-4 py-4 text-center text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Team
                  </th>
                </tr>
              </thead>
              <tbody>
                {compareSections.map((section) => (
                  <Fragment key={section.title}>
                    <tr className="bg-slate-100 dark:bg-slate-800/80">
                      <td
                        colSpan={4}
                        className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300"
                      >
                        {section.title}
                      </td>
                    </tr>
                    {section.rows.map((row) => (
                      <tr
                        key={row.feature}
                        className="border-t border-slate-100 dark:border-slate-800"
                      >
                        <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-200">
                          {row.feature}
                        </td>
                        <td className="px-4 py-4 text-center">
                          {renderCompareValue(row.starter)}
                        </td>
                        <td className="px-4 py-4 text-center">
                          {renderCompareValue(row.growth)}
                        </td>
                        <td className="px-4 py-4 text-center">
                          {renderCompareValue(row.team)}
                        </td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="flex justify-center">
          <Button
            variant="outline"
            className="h-11 rounded-xl border-slate-300 px-6 dark:border-slate-700"
            onClick={() => navigate({ to: '/clients' })}
          >
            Back to clients
          </Button>
        </div>
      </div>
    </div>
  )
}
